<template>
	<div>
		<div class="short_table_description">
			<div class="tertiary_color text_left"
				 v-html="$t('debtorManagement.revolvingPutOption.putOptionTable.headerTip', {url: instructionPdfUrl})">
			</div>
		</div>
		<div class="table_head">
			<div class="flex flex_between">
				<div>
					<md-button class="md-blank margin_r" @click="showAddPutOptionModal">
						<i class="fa fa-retweet"></i>
						{{ $t("debtorManagement.overview.requestPutOption") }}
					</md-button>
				</div>
			</div>
		</div>
		<div class="flex">
			<div class="flex flex_column w_100">
				<ag-item
					:rowData="rowData"
					:columnDefs="columnDefs"
					v-on:sortChanged="sortChanged"
					:with-tabs="true"
					:with-header="true"
					:with-short-description="true"
				></ag-item>
				<Pagination
					:totalNum="totalNum"
					:pageSize="pageSize"
					:sizeChange="sizeChange"
					:currentChange="currentChange"
					:currentPage="page"
				></Pagination>
			</div>
		</div>
		<RevolvingPutOptionModal ref="putOptionModal" @change="optionChanged"></RevolvingPutOptionModal>
	</div>
</template>
<script>

import AgItem from "@/components/templates/agGrid/AgItem.vue";
import Pagination from "@/components/templates/common/Pagination.vue";
import PaginationParent from "@/components/templates/common/PaginationParent.vue";

import {asLocalizedDate} from "@/components/templates/agGrid/formatter/valueFormatters";
import {asDecimal} from "@/components/templates/agGrid/formatter/valueFormatters";
import CofaceDraRenderer from "@/components/templates/agGrid/renderer/CofaceDraRenderer";
import {revolvingPutOptionsSellerApi} from "@/common/api/revolvingPutOptionsSellerApi";
import {enumDef} from "@/common/enumDefs";
import RevolvingPutOptionModal from "@/components/templates/seller/putOption/RevolvingPutOptionModal";
import {localization} from "@/common/lang/localization";

export default {
	extends: PaginationParent,
	components: { AgItem, Pagination, RevolvingPutOptionModal },
	data() {
		return {
			rowData: [],
			columnDefs: [],
			filters: [],
			sorting: [],
			instructionPdfLocales: ["en", "de"],
		};
	},
	beforeMount() {
		this.setColumnDefs();
		this.getList();
	},
	methods: {
		sortChanged(sortData) {
			this.sorting = sortData;
			this.initPage();
			this.getList();
		},
		setColumnDefs() {
			this.columnDefs = [
				{
					headerName: this.$t("common.debtor"),
					field: "debtorName",
					minWidth: 180,
					nameType: "debtorName",
					idType: "debtorId",
					cellRendererFramework: "CompanyTem",
					sortable: true,
				},
				{
					headerName: this.$t("common.cofaceDra"),
					headerComponentParams: {
						tooltip: this.$t("seller.sell.table.header.dra.tooltip"),
					},
					field: "cofaceDra",
					minWidth: 70,
					maxWidth: 70,
					cellRendererFramework: "CofaceDraRenderer",
					cellRendererParams: {
						draStatusField: "cofaceDraStatus"
					},
					sortable: true
				},
				{
					headerName: this.$t("common.transactionCode"),
					field: "referenceNumber",
					minWidth: 175,
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.optionStart"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.optionStart.tooltip"),
					},
					field: "startDate",
					minWidth: 120,
					valueFormatter: asLocalizedDate(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.optionEnd"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.optionEnd.tooltip"),
					},
					field: "endDate",
					minWidth: 120,
					valueFormatter: asLocalizedDate(),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.discount"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.discount.tooltip"),
					},
					headerClass: "align-right",
					field: "discountRate",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 150,
					valueFormatter: asDecimal({ showPercentSign: true }),
					sortable: true
				},
				{
					headerName: this.$t("revolvingPutOption.revolvingLine"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.revolvingLine.tooltip"),
					},
					headerClass: "align-right",
					field: "saleLimit",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 120,
					valueFormatter: asDecimal(),
					sortable: true
				},
				{
					headerName: this.$t("common.currency"),
					field: "currency",
					minWidth: 100,
					maxWidth: 100,
					sortable: true
				},
				{
					headerName: this.$t("debtorManagement.revolvingPutOption.putOptionTable.offerCount"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.offers.tooltip"),
					},
					field: "offersCount",
					minWidth: 110,
					maxWidth: 110,
					sortable: true
				},
				{
					headerName: this.$t("debtorManagement.revolvingPutOption.putOptionTable.minOfferPremium"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.minPremium.tooltip"),
					},
					headerClass: "align-right",
					field: "minOfferPremium",
					cellStyle: { "justify-content": "flex-end" },
					minWidth: 130,
					maxWidth: 150,
					valueFormatter: asDecimal({ showPercentSign: true }),
					sortable: true
				},
				{
					headerName: this.$t("common.status"),
					field: "status",
					minWidth: 150,
					maxWidth: 150,
					cellRendererFramework: "EnumAsText",
					enumDef: enumDef.revolvingPutOptionStatus,
				},
				{
					headerName: this.$t("common.timeLeft"),
					headerComponentParams: {
						tooltip: this.$t("revolvingPutOption.table.header.remainingIntervalTime.tooltip"),
					},
					minWidth: 170,
					maxWidth: 170,
					cellRendererFramework: "TimeLeftCountdown",
					cellRendererParams: {
						endTimeGetter: (data) =>
							data.status === enumDef.revolvingPutOptionStatus.Published ? data.publishingIntervalEndDate :
								data.status === enumDef.revolvingPutOptionStatus.ReadyToBeAccepted ? data.toBeAcceptedIntervalEndDate :
									""
					},
					sortable: true
				},
				{
					headerName: this.$t("common.actions"),
					minWidth: 100,
					maxWidth: 100,
					cellRendererFramework: "RevolvingPutOptionTableOperation",
					cellRendererParams: {
						statusField: "status",
						showEditPutOptionModal: this.showEditPutOptionModal,
						showPutOptionDetails: this.showPutOptionDetails,
						deletePutOption: this.deletePutOption,
						withdrawPutOption: this.withdrawPutOption,
					},
					pinned: "right"
				}

			];
		},
		optionChanged() {
			this.$emit("changed");
			this.getList();
		},
		getList() {
			let postData = {
				columns: [],
				filters: this.filters || [],
				maxResults: this.maxResults,
				offset: this.offset,
				sorting: this.sorting
			};
			revolvingPutOptionsSellerApi.inspection.listRequests(postData)
				.then(res => {
					this.rowData = res.data;
					this.setTotalNum(res.recordsTotal);
				})
				.catch(this.toast);
		},
		showAddPutOptionModal(data) {
			this.$refs.putOptionModal.showAddPutOptionModal(data);
		},
		showEditPutOptionModal(data) {
			this.$refs.putOptionModal.showEditPutOptionModal(data);
		},
		showPutOptionDetails(data) {
			this.$refs.putOptionModal.showPutOptionDetails(data);
		},
		withdrawPutOption(id) {
			this.$refs.putOptionModal.withdrawPutOption(id);
		},
		deletePutOption(id) {
			this.$refs.putOptionModal.deletePutOption(id);
		},
	},
	computed: {
		instructionPdfUrl() {
			let locale = localization.currentLocale();
			if (!this.instructionPdfLocales.includes(locale)) {
				locale = "en";
			}

			return locale === "de" ? "/vue/pdf/putoption/put_option_anleitung.pdf" : "/vue/pdf/putoption/put_option_instruction.pdf";
		}
	}
};
</script>